import { api } from '../console/api.ts';
import { empty, pageHead, pill } from '../console/components.ts';
import { date, esc, icon, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { isNubiOrg, supervises } from '../console/session.ts';
import { act } from '../console/ui.ts';
import { checkboxControl } from '../controls.ts';
import { focusPolicy } from './polizas.ts';
import { focusRenewal } from './renovaciones.ts';

/**
 * «Avisos»: what the person has to attend to, the channels they accept (e-mail, WhatsApp, SMS)
 * and, for management, what was sent by those channels. The menu's badge counts the unread.
 */

type NotificationView = {
  id: string;
  channel: string;
  recipient: string;
  template: string;
  title: string;
  body: string;
  link: string | null;
  status: string;
  providerRef: string | null;
  /** A QA stand-in channel took it: nothing left Nubi. */
  simulated?: boolean;
  error: string | null;
  createdAt: string;
  readAt: string | null;
};
const CHANNEL: Record<string, string> = {
  IN_APP: 'Nubi',
  EMAIL: 'Correo',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};
const DELIVERY: Record<string, string> = {
  DELIVERED: 'Entregado',
  SENT: 'Enviado',
  FAILED: 'Falló',
};

/**
 * Refreshes the unread badge in the menu, and in the phone's top bar (#237) (quietly: a failure
 * only hides the badge).
 */
export async function refreshBadge() {
  const badges = [
    ...document.querySelectorAll<HTMLElement>(
      '#nav [data-view="avisos"] .count, #nav-quick [data-view="avisos"] .count',
    ),
  ];
  // Avisos are an agency's: Nubi's own organization has none (ADR-0023).
  if (badges.length === 0 || isNubiOrg()) return;
  const r = await api<{ unread: number }>('GET', '/v1/notifications').catch(() => ({ unread: 0 }));
  for (const badge of badges) {
    badge.textContent = String(r.unread);
    badge.hidden = r.unread === 0;
  }
}

export async function avisos() {
  const [inbox, prefs] = await Promise.all([
    api<{ unread: number; items: NotificationView[] }>('GET', '/v1/notifications'),
    api<{ channels: Partial<Record<string, { enabled: boolean; consentedAt: string | null }>> }>(
      'GET',
      '/v1/notification-preferences',
    ),
  ]);
  let html = pageHead(
    'Avisos',
    'Lo que te toca atender, dentro de Nubi y por los canales que elijas. Solo te escribimos por correo, WhatsApp o SMS si lo aceptas.',
    inbox.unread
      ? `<button type="button" class="btn" data-read-all>${icon('ok')}Marcar todo como leído</button>`
      : '',
  );
  html += inbox.items.length
    ? `<section class="card"><ul class="inbox">${inbox.items
        .map(
          (
            n,
          ) => `<li class="${n.readAt ? '' : 'unread'}"><span class="dot" aria-hidden="true"></span>
          <div class="inbox-text"><strong>${esc(n.title)}</strong><span class="muted small">${esc(when(n.createdAt))}${n.readAt ? ' · leído' : ''}</span>
          ${n.body ? `<p>${esc(n.body)}</p>` : ''}</div>
          <div class="inbox-actions">${n.link ? `<button type="button" class="btn sm" data-go="${esc(n.link)}" data-read="${esc(n.id)}">Ir</button>` : ''}
          ${n.readAt ? '' : `<button type="button" class="btn sm" data-read="${esc(n.id)}" aria-label="Marcar como leído">Leído</button>`}</div></li>`,
        )
        .join('')}</ul></section>`
    : empty(
        'ok',
        'Estás al día',
        'Cuando algo requiera tu atención (una aseguradora pide información, una orden nueva…), aparecerá aquí.',
        '',
        2,
      );
  const ch = (c: 'EMAIL' | 'WHATSAPP' | 'SMS', label: string, hint: string) => {
    const on = !!prefs.channels[c]?.enabled;
    const since = prefs.channels[c]?.consentedAt;
    return checkboxControl(
      { class: 'check channel', 'data-channel': c, checked: on },
      `<span><strong>${label}</strong><span class="muted small">${on && since ? `Aceptado el ${esc(date(since))}` : hint}</span></span>`,
    );
  };
  html += `<section class="card"><div class="card-head"><h2>Tus canales</h2><span class="spacer"></span><span class="muted small">Los avisos dentro de Nubi siempre llegan.</span></div>
    <div class="channels">${ch('EMAIL', 'Correo', 'Recibir avisos en tu correo')}${ch('WHATSAPP', 'WhatsApp', 'Recibir avisos en tu WhatsApp')}${ch('SMS', 'SMS', 'Recibir avisos por mensaje de texto')}</div>
    <p class="muted small consent">Al activar un canal aceptas recibir avisos de trabajo por ese medio; puedes retirarlo cuando quieras.</p></section>`;
  if (supervises()) {
    const log = await api<NotificationView[]>('GET', '/v1/notifications/deliveries');
    html += log.length
      ? `<section class="card"><div class="card-head"><h2>Envíos por correo, WhatsApp y SMS</h2></div><div class="table-wrap" data-component="table"><table class="table">
        <thead><tr><th>Cuándo</th><th>Para</th><th>Canal</th><th>Aviso</th><th>Estado</th><th>Referencia</th></tr></thead><tbody>
        ${log
          .map(
            (
              n,
            ) => `<tr><td class="nowrap">${esc(when(n.createdAt))}</td><td>${esc(n.recipient.replace(/^agent:/, 'Agente ').replace(/^member:member-test-\w+-/, ''))}</td>
            <td>${esc(CHANNEL[n.channel] ?? n.channel)}</td><td>${esc(n.title)}</td>
            <td>${
              n.simulated
                ? pill('warn', 'Simulado')
                : pill(n.status === 'FAILED' ? 'bad' : 'ok', DELIVERY[n.status] ?? n.status)
            }${n.simulated ? '<div class="muted small">Ambiente de pruebas: no salió de Nubi.</div>' : ''}${n.error ? `<div class="muted small">${esc(n.error)}</div>` : ''}</td>
            <td class="mono small">${esc(n.providerRef ?? '—')}</td></tr>`,
          )
          .join('')}</tbody></table></div></section>`
      : '';
  }
  return html;
}

/**
 * Opens a view from a link: "polizas", "renovaciones?renovar=<id>" (a renewal notice: the
 * policy in «Renovaciones», its follow-up open; a notice sent before #308 says
 * "polizas?renovar=<id>" and opens «Renovaciones» too) or "polizas?poliza=<id>" (a policy's
 * page: the customer's page, #310).
 */
export async function go(link: string) {
  const [view = 'resumen', query = ''] = link.split('?');
  const params = new URLSearchParams(query);
  const renew = params.get('renovar');
  const policy = params.get('poliza');
  if (renew && (view === 'renovaciones' || view === 'polizas')) {
    await focusRenewal(renew);
    return;
  }
  if (view === 'polizas' && policy) {
    await focusPolicy(policy);
    return;
  }
  await render(view);
}

export const actions: Actions = {
  click: {
    readAll: (t) => {
      void act(t, 'Marcando…', async () => {
        const r = await api<{ read: number }>('POST', '/v1/notifications/read', {});
        await render(current, { quiet: true });
        return `${r.read} avisos marcados como leídos.`;
      });
    },
    read: (t, d) => {
      // «Ir»: marks it read on the way (quietly) and opens what it is about.
      if (d.go) {
        if (d.read) void api('POST', '/v1/notifications/read', { id: d.read }).catch(() => {});
        void go(d.go);
        return;
      }
      if (!d.read) return;
      void act(t, 'Marcando…', async () => {
        await api('POST', '/v1/notifications/read', { id: d.read });
        await render(current, { quiet: true });
        return 'Aviso marcado como leído.';
      });
    },
  },
  changeData: {
    channel: (control) => {
      const box = control as unknown as HTMLInputElement;
      const channel = box.dataset.channel;
      if (!channel) return;
      void act(box, 'Guardando tu preferencia…', async () => {
        await api('PUT', '/v1/notification-preferences', { channel, enabled: box.checked });
        await render(current, { quiet: true });
        return box.checked
          ? `Recibirás avisos por ${CHANNEL[channel]}.`
          : `Ya no recibirás avisos por ${CHANNEL[channel]}.`;
      });
    },
  },
};
